import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mantine/core";
import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { IconFilterOff } from "@tabler/icons-react";

import { getCategories, getDays, getMonths } from "@data/timelineFilters";

import { StyledDialogOverlay } from "@styles/components/Dialog.styled";

import { StyledTimelineFilterDialogContent } from "./TimelineFilterDialog.styled";


interface FilterDialogProps {
  filters: { year?: string; month?: string; day?: string; category?: string };
  setFilters: (arg: FilterDialogProps["filters"]) => void;
  years: string[];
}

export const TimelineFilterDialog = ({
  filters,
  setFilters,
  years,
}: FilterDialogProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (filters?.year || filters?.month || filters?.day || filters?.category) {
      setFilters({
        year: filters?.year || "",
        month: filters?.month || "",
        day: filters?.day || "",
        category: filters?.category || "",
      });
    }
  }, [filters]);

  return (
    <Dialog.Portal>
      <StyledDialogOverlay />

      <StyledTimelineFilterDialogContent>
        <div className="dialog-header">
          <Dialog.Title className="DialogTitle">
            {t("timeline-filters")}
          </Dialog.Title>

          <Dialog.Close asChild>
            <button className="IconButton" aria-label="Close">
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </div>

        <div className="filters-wrapper">
          <form className="filter-column">
            <p>{t("year")}</p>
            <RadioGroup.Root
              className="RadioGroupRoot"
              // defaultValue={years[0]}
              onValueChange={value => {
                setFilters({ year: value });
              }}
              value={filters.year}
              aria-label="Years">
              <button
                className="clear-filter-btn"
                type="button"
                onClick={() => {
                  setFilters({ year: "" });
                }}>
                {t("clear")}
              </button>
              {years.map((year, index) => (
                <div key={index}>
                  <RadioGroup.Item
                    className="RadioGroupItem"
                    value={year}
                    id={year}>
                    <RadioGroup.Indicator className="RadioGroupIndicator" />
                  </RadioGroup.Item>
                  <label htmlFor={year}>{year}</label>
                </div>
              ))}
            </RadioGroup.Root>
          </form>

          <form className="filter-column">
            <p>{t("month")}</p>
            <RadioGroup.Root
              className="RadioGroupRoot"
              // defaultValue={months[0]}
              onValueChange={value => {
                setFilters({ month: value });
              }}
              value={filters.month}
              aria-label="Months">
              <button
                className="clear-filter-btn"
                type="button"
                onClick={() => {
                  setFilters({ month: "" });
                }}>
                {t("clear")}
              </button>
              {getMonths().map((month, index) => (
                <div key={index}>
                  <RadioGroup.Item
                    className="RadioGroupItem"
                    value={month.value}
                    id={month.value}>
                    <RadioGroup.Indicator className="RadioGroupIndicator" />
                  </RadioGroup.Item>
                  <label htmlFor={month.value}>{month.label}</label>
                </div>
              ))}
            </RadioGroup.Root>
          </form>

          <form className="filter-column">
            <p>{t("day")}</p>
            <RadioGroup.Root
              className="RadioGroupRoot"
              // defaultValue={days[0]}
              onValueChange={value => {
                setFilters({ day: value });
              }}
              value={filters.day}
              aria-label="Day of the week">
              <button
                className="clear-filter-btn"
                type="button"
                onClick={() => {
                  setFilters({ day: "" });
                }}>
                {t("clear")}
              </button>
              {getDays().map((day, index) => (
                <div key={index}>
                  <RadioGroup.Item
                    className="RadioGroupItem"
                    value={day.value}
                    id={day.value}>
                    <RadioGroup.Indicator className="RadioGroupIndicator" />
                  </RadioGroup.Item>
                  <label htmlFor={day.value}>{day.label}</label>
                </div>
              ))}
            </RadioGroup.Root>
          </form>

          <form className="filter-column">
            <p>{t("category")}</p>
            <RadioGroup.Root
              className="RadioGroupRoot"
              // defaultValue={categories[0]}
              onValueChange={value => {
                setFilters({ category: value });
              }}
              value={filters.category}
              aria-label="Category">
              <button
                className="clear-filter-btn"
                type="button"
                onClick={() => {
                  setFilters({ category: "" });
                }}>
                {t("clear")}
              </button>
              {getCategories().map((category, index) => (
                <div key={index}>
                  <RadioGroup.Item
                    className="RadioGroupItem"
                    value={category.value}
                    id={category.value}>
                    <RadioGroup.Indicator className="RadioGroupIndicator" />
                  </RadioGroup.Item>
                  <label htmlFor={category.value}>{category.label}</label>
                </div>
              ))}
            </RadioGroup.Root>
          </form>
        </div>

        <div className="dialog-footer">
          <Button
            variant="transparent"
            size="sm"
            onClick={() =>
              setFilters({ year: "", month: "", day: "", category: "" })
            }
            leftSection={<IconFilterOff size={16} />}>
            {t("clear-filters")}
          </Button>
          <Dialog.Close asChild>
            <Button>{t("done")}</Button>
          </Dialog.Close>
        </div>
      </StyledTimelineFilterDialogContent>
    </Dialog.Portal>
  );
};
